import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useRegion } from '../hooks/useRegion';
import { formatCurrency, hasCrewSuper } from '../lib/regionConfig';
import { Button } from '../components/ui/button';
import { Illustration } from '../components/Illustration';
import { Logo } from '../components/Logo';
import { ArrowRight, Loader2, Play } from 'lucide-react';
import templateKit from './landing/templateKit.json';
import { kitStory } from './landing/kitStory';
import { JobToGear } from './landing/JobToGear';
import { GearInsights } from './landing/Visuals';
import { KitOrbit } from './landing/KitOrbit';
import { BriefFill } from './landing/BriefFill';
import { ShotListDrop } from './landing/ShotListDrop';
import { HelixCorridor } from './landing/HelixCorridor';
import { JobLifecycle } from './landing/JobLifecycle';
import { Faq } from './landing/Faq';
import { PlanBoxes } from './landing/PlanBoxes';
import { useStartDemo } from '../components/demo/useStartDemo';
import { Chapter, ChapterBars, coveredIn } from './landing/Chapter';
import { CrewAndSuppliers, PurchaseOrders } from './landing/Teams';

// The pictures on this page are drawn from the demo template account's kit
// (scripts/snapshot_landing_kit.py): drawings and a figure or two, not screens.
const { equipment, kits } = templateKit;
const story = kitStory(templateKit);
// The payback tool shows the kit with the furthest to go
const paybackKit = [...kits].sort(
  (a, b) => (b.total_purchase_price - b.total_income) - (a.total_purchase_price - a.total_income),
)[0];

const WRAP = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8';


// Every section: a short headline with the idea, and one
// sentence under it with the fact.
function SectionHead({ title, children, inverse = false, className = '' }) {
  return (
    <div className={`flex flex-col gap-5 max-w-3xl ${className}`}>
      <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-balance">
        {title}
      </h2>
      <p className={`text-lg sm:text-xl leading-snug ${inverse ? 'text-inverse-muted' : 'text-muted-foreground'}`}>
        {children}
      </p>
    </div>
  );
}

function DemoButton({ onClick, loading, className = '', ...props }) {
  return (
    <Button size="lg" className={`h-12 px-8 text-base ${className}`} onClick={onClick} disabled={loading} {...props}>
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
      Try the demo
    </Button>
  );
}

export default function LandingPage() {
  const { isAuthenticated } = useAuthStore();
  const { region } = useRegion();

  // Headline figures are whole units in the viewer's own currency
  const money = (amount) => formatCurrency(amount, region, { whole: true });
  const percent = (value) => `${Math.round(value)}%`;
  // Crew super is Australian, so only an Australian visitor hears about it
  const crewSuper = hasCrewSuper(region);

  const { start: handleTryDemo, loading: demoLoading } = useStartDemo();


  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col tabular-nums">
      {/* Header */}
      {/* Solid: the sections pin just below it, so only the tall band would show through */}
      <header className="border-b border-line bg-background sticky top-0 z-50">
        <div className={`${WRAP} h-16 flex items-center justify-center md:justify-between`}>
          <Link to="/" className="flex items-center gap-3">
            <Logo className="w-9 h-9" alt="" />
            <span className="font-medium text-lg">No Half Days</span>
          </Link>
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <Button asChild data-testid="go-to-dashboard-btn">
                <Link to="/dashboard">Go to dashboard</Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" asChild data-testid="header-login-btn">
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button onClick={handleTryDemo} disabled={demoLoading} data-testid="header-try-demo-btn">
                  {demoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Try the demo
                </Button>
                <Button variant="secondary" asChild data-testid="header-register-btn">
                  <Link to="/register">Create account</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* The chapters' bars, once reached, stacked under the header */}
      <ChapterBars />

      {/* Hero */}
      {/* Height follows the illustration's 21:9 so its props are not clipped */}
      <section className="relative overflow-hidden bg-background flex items-center lg:min-h-[min(43vw,760px)]">
        {/* The kit check, as its two groups in the corners, a little above the
            hero's middle and behind the plan boxes. Desktop only. */}
        <div className="absolute top-[33%] -translate-y-1/2 left-0 hidden lg:block w-[30vw] max-w-[540px] aspect-[984/1018]">
          <Illustration name="hero" className="h-full w-full aspect-auto object-cover object-left-bottom" />
        </div>
        <div className="absolute top-[33%] -translate-y-1/2 right-0 hidden lg:block w-[30vw] max-w-[540px] aspect-[984/1018]">
          <Illustration name="hero" className="h-full w-full aspect-auto object-cover object-right-bottom" />
        </div>

        <div className={`relative w-full ${WRAP} py-20 lg:py-32`}>
          <div className="text-center max-w-4xl mx-auto flex flex-col items-center gap-7">
            <h1 className="text-[2.5rem] sm:text-6xl font-light tracking-tight leading-[1.02] [text-wrap:balance]">
              Quoting for film and TV crews <span className="text-clay">who own their own gear.</span>
            </h1>
            {/* Narrow enough on desktop to stay clear of the art in the corners */}
            <p className="text-xl sm:text-2xl lg:text-xl text-muted-foreground max-w-2xl lg:max-w-[33rem] leading-snug [text-wrap:balance]">
              See when every camera, lens and light has paid for itself.
            </p>
            <div className="mt-3 flex flex-col sm:flex-row gap-3 justify-center">
              <DemoButton onClick={handleTryDemo} loading={demoLoading} data-testid="hero-try-demo-btn" />
              <Button size="lg" variant="outline" className="h-12 px-8 text-base" asChild>
                <Link to="/register">
                  Create free account <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
          <PlanBoxes />
        </div>
      </section>

      {/* Solo: the gear, as an investment */}
      <Chapter plan="solo" under="bg-background">
        <section className="bg-inverse text-inverse-foreground">
          {/* The curve runs the section's full width, behind its heading */}
          <KitOrbit story={story} equipment={equipment} money={money} wrap={WRAP}>
            <SectionHead inverse title={<>Bought. Booked.<br /> Nearly paid for itself.</>}>
              Track every investment, know when you’ve made a return.
            </SectionHead>
          </KitOrbit>
        </section>

        {/* How it adds up: a job wraps and its lines drop into the gear */}
        {story.wrap && (
          <section className="bg-surface">
            {/* A tighter gap: the animation keeps its own room above the cards */}
            <div className={`${WRAP} py-14 sm:py-20 lg:py-32 flex flex-col gap-6 sm:gap-8`}>
              <SectionHead title="Every job counts.">
                Mark a job complete. Every quoted line item goes back to the gear’s return on investment.
              </SectionHead>
              <JobToGear wrap={story.wrap} money={money} />
            </div>
          </section>
        )}

        {/* What's working, and what isn't */}
        <section className="bg-background">
          <div className={`${WRAP} py-20 lg:py-32 flex flex-col gap-12 lg:gap-16`}>
            <SectionHead title={<>Know what’s working.<br /> And what isn’t.</>}>
              Every piece of kit, weighed against what it cost.
            </SectionHead>
            <GearInsights story={story} equipment={equipment} kit={paybackKit} money={money} percent={percent} />
          </div>
        </section>
      </Chapter>

      {/* Filmmaker: winning the work */}
      <Chapter plan="filmmaker" under="bg-background">
        {/* The job: everything on one record, from brief to Xero. A chapter
            opens on the dark band */}
        <section className="bg-inverse text-inverse-foreground">
          <div className={`${WRAP} py-20 lg:py-32 flex flex-col gap-12 lg:gap-16`}>
            <SectionHead inverse title={<>One job.<br /> Everything on it.</>}>
              Brief, shot list, hires and estimate. From first question to sent for payment.
            </SectionHead>
            {story.wrap && <JobLifecycle inverse jobNumber={story.wrap.job.job_number} />}
          </div>
        </section>

        {/* Treatments: every reference, from anywhere, flying in (BYQ's helix) */}
        {/* Dark like the job above it, so a faint line marks where one ends */}
        <section className="bg-inverse text-inverse-foreground border-t border-inverse-foreground/10 [overflow:clip]">
          <HelixCorridor covered={coveredIn('filmmaker')} />
        </section>

        {/* Before the job: the client's brief and the director's shot list */}
        <section className="bg-background">
          <div className={`${WRAP} py-20 lg:py-32 flex flex-col gap-12 lg:gap-16`}>
            <SectionHead title={<>Brief in.<br /> Shot list out.</>}>
              Clients fill in your brief from a link, no account needed. Build the shot list frame by frame,
              and its gear goes straight onto the quote.
            </SectionHead>
            <div className="grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-6 lg:gap-8 items-start">
              <BriefFill money={money} />
              <ShotListDrop />
            </div>
          </div>
        </section>
      </Chapter>

      {/* Teams: delivering it, with a crew */}
      <Chapter plan="teams" under="bg-background">
        {/* Crew and suppliers. A chapter opens on the dark band */}
        <section className="bg-inverse text-inverse-foreground">
          <div className={`${WRAP} py-20 lg:py-32 flex flex-col gap-12 lg:gap-16`}>
            <SectionHead inverse title={<>Your crew.<br /> Your suppliers.</>}>
              Book the crew onto the job, day by day. The gear you don’t own, hired in from your
              suppliers.
            </SectionHead>
            <CrewAndSuppliers />
          </div>
        </section>

        {/* Purchase orders, then the books */}
        <section className="bg-surface">
          <div className={`${WRAP} py-20 lg:py-32 flex flex-col gap-12 lg:gap-16`}>
            <SectionHead title={<>Send POs.<br /> Connect to your accounting.</>}>
              A purchase order for every crew member, in one click. Their bills go straight to Xero
              {crewSuper ? ', with super worked out.' : '.'}
            </SectionHead>
            <PurchaseOrders crewSuper={crewSuper} />
          </div>
        </section>
      </Chapter>

      {/* Questions: the last panel, what stops someone signing up */}
      {/* It scrolls like the chapters; only their bars stay */}
      <section className="relative bg-surface" style={{ zIndex: 5 }}>
        <div className="bg-background rounded-t-[32px] lg:rounded-t-[48px]">
        <div className={`${WRAP} py-20 lg:py-32 grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-12 lg:gap-16 items-start`}>
          <SectionHead title={<>Questions?<br /> Answered.</>}>
            Anything else, ask us from Help once you’re in. A person answers.
          </SectionHead>
          <Faq />
        </div>
        </div>
      </section>

      {/* Close, and the footer, on the curtain */}
      {/* The footer is last, so it scrolls up over the pile and stays */}
      <div className="relative z-[20] mt-auto bg-background">
      <footer className="bg-inverse text-inverse-foreground rounded-t-[48px] lg:rounded-t-curtain">
        <div className={`${WRAP} pt-24 lg:pt-32 pb-10 flex flex-col gap-16`}>
          <div className="flex flex-col gap-6 items-start">
            <h2 className="text-6xl sm:text-8xl lg:text-9xl font-light tracking-tight leading-[0.95]">no half days.</h2>
            <p className="text-xl text-inverse-muted max-w-xl">
              Your gear already works hard. Now you’ll know it pays.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <DemoButton
                onClick={handleTryDemo}
                loading={demoLoading}
                className="bg-inverse-foreground text-inverse hover:bg-inverse-foreground/90"
              />
              <Button
                size="lg"
                variant="outline"
                className="h-12 px-8 text-base bg-transparent text-inverse-foreground border-inverse-muted hover:bg-inverse-foreground/10 hover:text-inverse-foreground"
                asChild
                data-testid="hero-get-started-btn"
              >
                <Link to="/register">
                  Create free account <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
            <p className="text-sm text-inverse-muted">
              Already have an account?{' '}
              <Link to="/login" className="text-inverse-foreground hover:underline">Sign in</Link>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-inverse-muted/40">
            <div className="flex items-center gap-2">
              <Logo className="w-5 h-5" alt="" onInverse />
              <span className="font-medium">No Half Days</span>
              <span className="text-sm italic text-inverse-muted">beta</span>
            </div>
            <nav aria-label="Legal" className="flex flex-wrap justify-center items-center gap-4 text-sm text-inverse-muted">
              <Link to="/terms" className="hover:text-inverse-foreground transition-colors">Terms of service</Link>
              <Link to="/privacy" className="hover:text-inverse-foreground transition-colors">Privacy policy</Link>
              <Link to="/refund-policy" className="hover:text-inverse-foreground transition-colors">Refund policy</Link>
            </nav>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}
